<script setup lang="ts">
import UIBanner from '@/components/ui/UIBanner.vue'
import useLanguage, { useInlineLanguage } from '@/composables/useLanguage'
import { useGetWorkshopCurrentRoute } from '@/services/useGetWorkshop'
import { getWeightedTotalCost } from '@/utils/remedy'
import { IMappedRemedy } from '@lifk/types'
import { roundAndTruncate } from '@lifk/utils'
import { PhArrowSquareDown, PhArrowSquareUp } from '@phosphor-icons/vue'
import { computed } from 'vue'

const props = defineProps<{
  /** What remedy was added/updated by the user? */
  newMappedRemedy: IMappedRemedy
  /** What old remedy does the new remedy replace? */
  oldMappedRemedy?: IMappedRemedy
  /** Was the remedy added within the site polygon? */
  addedToSketchSite: boolean
}>()

const i18n = useLanguage('EffectsBanner')
const { t } = useInlineLanguage()
const { data: workshop } = useGetWorkshopCurrentRoute()

const footprintWasEdited = computed(
  () =>
    !!props.oldMappedRemedy &&
    props.oldMappedRemedy.footprintAreaSqFt !==
      props.newMappedRemedy.footprintAreaSqFt
)

const bannerText = computed(() => {
  const translatedName = t(props.newMappedRemedy.remedy.nameI18n)
  if (!props.newMappedRemedy.isWithinPolygon) {
    return i18n.value.remedyOutsidePolygon
  } else if (footprintWasEdited.value && props.oldMappedRemedy) {
    const footprintDiff =
      props.newMappedRemedy.footprintAreaSqFt -
      props.oldMappedRemedy.footprintAreaSqFt
    if (footprintDiff < 0) {
      return `${i18n.value.sqftRemoved1} ${roundAndTruncate(
        footprintDiff * -1
      )} ${i18n.value.sqftRemoved2} ${translatedName}`
    } else {
      return `${i18n.value.sqftAdded1} ${roundAndTruncate(footprintDiff)} ${
        i18n.value.sqftAdded2
      } ${translatedName}`
    }
  } else if (props.addedToSketchSite) {
    return `${translatedName} ${i18n.value.added}`
  }

  return null
})

const totalCost = computed(() => {
  const newRemedyCost = getWeightedTotalCost(
    props.newMappedRemedy.remedy,
    props.newMappedRemedy.footprintAreaSqFt
  )

  if (footprintWasEdited.value && props.oldMappedRemedy) {
    const costDiff =
      newRemedyCost -
      getWeightedTotalCost(
        props.oldMappedRemedy.remedy,
        props.oldMappedRemedy.footprintAreaSqFt
      )
    return costDiff
  } else {
    return newRemedyCost
  }
})
const costText = computed(
  () => `$${roundAndTruncate(Math.abs(totalCost.value))}`
)
</script>

<template>
  <UIBanner v-if="bannerText" variant="white">
    <div
      class="flex place-content-center font-normal flex-col lg:flex-row lg:space-x-[10px]"
      data-testid="effects-banner"
    >
      <div
        class="flex place-content-center items-center space-x-[10px] text-body"
      >
        <div>{{ bannerText }}</div>
        <div
          v-if="
            totalCost !== 0 &&
            newMappedRemedy.isWithinPolygon &&
            !workshop?.hideCosts
          "
          class="flex space-x-[5px] place-content-center items-center"
        >
          <PhArrowSquareDown
            v-if="totalCost < 0"
            size="24px"
            class="fill-coulter-tint-1"
            weight="fill"
          />
          <PhArrowSquareUp
            v-else-if="totalCost > 0"
            size="24px"
            class="fill-manzanita"
            weight="fill"
          />
          <p :class="totalCost < 0 ? 'text-coulter-tint-1' : 'text-manzanita'">
            {{ costText }}
          </p>
        </div>
      </div>
    </div>
  </UIBanner>
</template>
